import AddIcon from "@atlaskit/icon/core/add";
import EditIcon from "@atlaskit/icon/core/edit";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import type {ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import type {EntityProfileFooterActionId} from "~/components/entityProfile/types";
import {ScheduleCallIcon} from "~/components/svgs";
import "~/components/table/table.css";

const FOOTER_ACTION_CONFIG: Record<
    EntityProfileFooterActionId,
    {
        label: string;
        icon: ReactNode;
    }
> = {
    logCall: {label: "Log a call", icon: <OnCallIcon label="" />},
    edit: {label: "Edit", icon: <EditIcon label="" />},
    create: {label: "Create", icon: <AddIcon label="" />},
    scheduleCall: {label: "Schedule", icon: <ScheduleCallIcon size={20} />},
    more: {label: "More", icon: <ShowMoreHorizontalIcon label="" />},
};

/**
 * Page action footer. Same 58px raised chrome as the workspace sidebar logo bar,
 * day-plan focus footer, and table page footer so the top separator meets the
 * sidebar (and aside panel footer) in one continuous line.
 */
export function EntityProfileFooterBar({
    actionIds,
    disabledActionIds = [],
    onAction,
    className,
    edgeToEdge = true,
}: {
    actionIds: Array<EntityProfileFooterActionId>;
    disabledActionIds?: Array<EntityProfileFooterActionId>;
    onAction: (actionId: EntityProfileFooterActionId) => void;
    className?: string;
    /** Page footer bleeds to match table chrome; aside should keep inset width. */
    edgeToEdge?: boolean;
}) {
    if (actionIds.length === 0) {
        return null;
    }

    const bar = (
        <div
            className={twJoin(
                "box-border grid h-[58px] min-h-[58px] w-full grid-flow-col auto-cols-fr items-stretch",
                "bg-ds-surface-raised",
                "pb-[max(0px,env(safe-area-inset-bottom))]",
            )}
        >
            {actionIds.map((actionId) => {
                const config = FOOTER_ACTION_CONFIG[actionId];
                const isDisabled = disabledActionIds.includes(actionId);

                return (
                    <button
                        key={actionId}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                            onAction(actionId);
                        }}
                        className={twJoin(
                            "grid justify-items-center content-center gap-y-ds-space-050 px-ds-space-050",
                            "bg-transparent cursor-pointer text-ds-text",
                            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-border-accent-blue",
                            isDisabled
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-ds-background-neutral-hovered",
                        )}
                    >
                        <span
                            className="grid place-items-center shrink-0 text-ds-icon [&_svg]:text-current"
                            aria-hidden
                        >
                            {config.icon}
                        </span>
                        <span className="text-xs font-medium leading-none whitespace-nowrap">
                            {config.label}
                        </span>
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className={twJoin("relative z-10 w-full shrink-0 border-t border-solid border-ds-border", className)}>
            {edgeToEdge === true ? (
                <div className="table-page-footer-bleed table-page-footer-bleed--edge-to-edge">{bar}</div>
            ) : (
                bar
            )}
        </div>
    );
}
